import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogTrigger,
} from "./ui/dialog";
import { Loader2, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { QUOTE_DIALOG } from "../constants/testIds";
import { VEHICLES } from "../data/vehicles";
import { api } from "../lib/api";
import { trackEvent, toPlatformWhatsAppUrl } from "../lib/whatsapp";

import BookingPolicyNotice from "./BookingPolicyNotice";
export default function InstantQuoteDialog({ trigger }) {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        name: "",
        mobile: "",
        pickup: "",
        drop: "",
        vehicle: "",
        trip_date: "",
    });

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.mobile || !form.pickup) {
            toast.error("Please fill Name, Mobile and Pickup.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await api.createQuote(form);
            trackEvent("quote_form_submit", { source: "instant-quote" });
            toast.success(res.message || "Quote request sent!");
            if (res.whatsapp_url) {
                setTimeout(() => {
                    window.open(toPlatformWhatsAppUrl(res.whatsapp_url), "_blank", "noopener,noreferrer");
                }, 400);
            }
            setForm({
                name: "",
                mobile: "",
                pickup: "",
                drop: "",
                vehicle: "",
                trip_date: "",
            });
            setOpen(false);
        } catch (err) {
            toast.error("Could not submit. Please try WhatsApp directly.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            {React.createElement(DialogTrigger, { asChild: true }, trigger || (
                    <button
                        type="button"
                        data-testid={QUOTE_DIALOG.trigger}
                        className="btn-outline-navy"
                        onClick={() => trackEvent("quote_button_click", { source: "hero" })}
                    >
                        <Sparkles className="h-4 w-4" /> Request a Quote
                    </button>
                ))}
            <DialogContent
                data-testid={QUOTE_DIALOG.root}
                className="max-h-[90dvh] overflow-y-auto sm:max-w-lg"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl font-extrabold text-brand-navy">
                        Request a Quote
                    </DialogTitle>
                    <DialogDescription className="text-sm text-brand-slate">
                        Share your trip details. Our team will confirm vehicle availability and the complete fare on WhatsApp.
                    </DialogDescription>
                </DialogHeader>

                <form
                    onSubmit={onSubmit}
                    className="grid grid-cols-1 gap-3 sm:grid-cols-2"
                >
                    <input
                        data-testid={QUOTE_DIALOG.name}
                        type="text"
                        required
                        placeholder="Full Name*"
                        value={form.name}
                        onChange={set("name")}
                        className="rounded-lg border border-brand-navy/10 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/40 sm:col-span-1"
                    />
                    <input
                        data-testid={QUOTE_DIALOG.mobile}
                        type="tel"
                        required
                        placeholder="Mobile Number*"
                        value={form.mobile}
                        onChange={set("mobile")}
                        className="rounded-lg border border-brand-navy/10 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/40 sm:col-span-1"
                    />
                    <input
                        data-testid={QUOTE_DIALOG.pickup}
                        type="text"
                        required
                        placeholder="Pickup Location*"
                        value={form.pickup}
                        onChange={set("pickup")}
                        className="rounded-lg border border-brand-navy/10 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/40 sm:col-span-2"
                    />
                    <input
                        data-testid={QUOTE_DIALOG.drop}
                        type="text"
                        placeholder="Drop Location"
                        value={form.drop}
                        onChange={set("drop")}
                        className="rounded-lg border border-brand-navy/10 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/40 sm:col-span-2"
                    />
                    <select
                        data-testid={QUOTE_DIALOG.vehicle}
                        value={form.vehicle}
                        onChange={set("vehicle")}
                        className="rounded-lg border border-brand-navy/10 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/40 sm:col-span-1"
                    >
                        <option value="">Preferred Vehicle</option>
                        {VEHICLES.map((v) => (
                            <option key={v.slug} value={v.name}>
                                {v.name}
                            </option>
                        ))}
                    </select>
                    <input
                        data-testid={QUOTE_DIALOG.date}
                        type="date"
                        value={form.trip_date}
                        onChange={set("trip_date")}
                        className="rounded-lg border border-brand-navy/10 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/40 sm:col-span-1"
                    />

                    <BookingPolicyNotice className="sm:col-span-2" />
                    <button
                        type="submit"
                        data-testid={QUOTE_DIALOG.submit}
                        disabled={submitting}
                        className="btn-gold sm:col-span-2 disabled:opacity-60"
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                            </>
                        ) : (
                            <>
                                <Send className="h-4 w-4" /> Get Quote on WhatsApp
                            </>
                        )}
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
}
